import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Button } from '../ui';
import { useScrollReveal } from '../../hooks';
import './ReferralProgram.css';

const REFERRAL_REWARD = 50;
const FRIEND_DISCOUNT = 30;

/**
 * Referral Program component
 * Allows users to refer friends and earn rewards
 */
const ReferralProgram: React.FC = () => {
	const { t } = useTranslation();
	const { ref, isVisible } = useScrollReveal({ threshold: 0.2 });
	const [email, setEmail] = useState('');
	const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			setStatus('error');
			return;
		}

		setStatus('loading');

		// Simulate API call
		await new Promise((resolve) => setTimeout(resolve, 1000));
		setStatus('success');
		setEmail('');
	};

	return (
		<section
			className="referral-program"
			ref={ref as React.RefObject<HTMLElement>}
		>
			<div className={`referral-container ${isVisible ? 'visible' : ''}`}>
				<div className="referral-content">
					<div className="referral-badge">🎁</div>
					<h2>{t('referral.title', 'Refer Friends, Earn Rewards')}</h2>
					<p>{t('referral.subtitle', 'Share the hibachi love and get rewarded!')}</p>
				</div>

				<div className="referral-grid">
					<div className="referral-step">
						<div className="step-icon">1</div>
						<h3>{t('referral.step1Title', 'Share Your Code')}</h3>
						<p>{t('referral.step1Desc', 'Share your unique referral code with friends and family')}</p>
					</div>

					<div className="referral-step">
						<div className="step-icon">2</div>
						<h3>{t('referral.step2Title', 'Friend Books')}</h3>
						<p>{t('referral.step2Desc', `Your friend gets $${FRIEND_DISCOUNT} off their first booking`)}</p>
					</div>

					<div className="referral-step">
						<div className="step-icon">3</div>
						<h3>{t('referral.step3Title', 'You Get Rewarded')}</h3>
						<p>{t('referral.step3Desc', `Earn $${REFERRAL_REWARD} credit for your next event`)}</p>
					</div>
				</div>

				<div className="referral-rewards">
					<div className="reward-card you">
						<span className="reward-amount">${REFERRAL_REWARD}</span>
						<span className="reward-label">{t('referral.yourReward', 'Your Reward')}</span>
					</div>
					<div className="reward-separator">+</div>
					<div className="reward-card friend">
						<span className="reward-amount">${FRIEND_DISCOUNT}</span>
						<span className="reward-label">{t('referral.friendDiscount', 'Friend\'s Discount')}</span>
					</div>
				</div>

				<div className="referral-form-container">
					{status !== 'success' ? (
						<form className="referral-form" onSubmit={handleSubmit}>
							<h3>{t('referral.getCode', 'Get Your Referral Code')}</h3>
							<div className="form-row">
								<Input
									type="email"
									placeholder={t('referral.emailPlaceholder', 'Enter your email')}
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									disabled={status === 'loading'}
									error={status === 'error' ? t('referral.invalidEmail', 'Please enter a valid email') : undefined}
								/>
								<Button
									type="submit"
									variant="primary"
									disabled={status === 'loading'}
								>
									{status === 'loading' ? (
										<span className="loading-spinner"></span>
									) : (
										t('referral.getCodeBtn', 'Get Code')
									)}
								</Button>
							</div>
						</form>
					) : (
						<div className="referral-success">
							<div className="success-icon">✓</div>
							<h3>{t('referral.successTitle', 'Your Referral Code is Ready!')}</h3>
							<p>{t('referral.successDesc', 'Check your email for your unique referral code and instructions.')}</p>
							<div className="sample-code">
								<span>HIBACHI</span>
								<span className="code-suffix">XXXX</span>
							</div>
						</div>
					)}
				</div>

				<div className="referral-note">
					<p>
						{t('referral.terms', '* Rewards apply after your friend completes their first booking. No limit on referrals!')}
					</p>
				</div>
			</div>
		</section>
	);
};

export default ReferralProgram;




